import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { resetPassword, fmtError } from "@/lib/api";
import { Loader2, KeyRound } from "lucide-react";

const fieldCls =
  "w-full bg-white border border-black/10 rounded-2xl px-4 py-3.5 text-sm text-[#1A1A1A] placeholder:text-[#A3A3A3] focus:outline-none focus:border-[#A78BFA] focus:ring-2 focus:ring-[#A78BFA]/15 transition-colors";

export default function ResetPasswordPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd !== confirm) { setError("Le due password non coincidono"); return; }
    if (pwd.length < 8) { setError("La password deve avere almeno 8 caratteri"); return; }
    setSubmitting(true);
    try {
      await resetPassword(token, pwd);
      setDone(true);
      setTimeout(() => navigate(`/${slug}/login`, { replace: true }), 2500);
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell testId="reset-page" subtitle="Recupero password" title="Link non valido." footer={
        <Link to={`/${slug}/forgot-password`} className="text-[#1A1A1A] underline underline-offset-4">
          Richiedi un nuovo link
        </Link>
      }>
        <p className="text-sm text-[#4A4A4A] font-light" data-testid="reset-no-token">
          Questo link è incompleto. Apri quello che hai ricevuto via email, oppure richiedine uno nuovo.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testId="reset-page"
      subtitle="Recupero password"
      title="Scegli la nuova password."
      footer={
        <Link to={`/${slug}/login`} className="text-[#1A1A1A] underline underline-offset-4">
          Torna all'accesso
        </Link>
      }
    >
      {done ? (
        <div className="text-center" data-testid="reset-done">
          <div className="h-14 w-14 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] flex items-center justify-center">
            <KeyRound className="text-white" size={26} />
          </div>
          <p className="text-[#1A1A1A]">Password aggiornata!</p>
          <p className="mt-2 text-sm text-[#4A4A4A] font-light">Ti sto portando alla pagina di accesso…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Nuova password (min 8 caratteri)</span>
            <input data-testid="reset-password" type="password" required autoComplete="new-password"
              value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" className={fieldCls} />
          </label>
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Ripeti la nuova password</span>
            <input data-testid="reset-confirm" type="password" required autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className={fieldCls} />
          </label>
          {error && (
            <div data-testid="reset-error" className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-2xl px-4 py-2.5">
              {error}
            </div>
          )}
          <button type="submit" data-testid="reset-submit" disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-60 text-white rounded-full px-6 py-3.5 text-sm tracking-wide">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? "Salvataggio…" : "Imposta la nuova password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
